import { Link } from "react-router-dom";
import { ShieldCheck, FileSearch, MessageSquareText, Wallet, Clock, ArrowRight, Sparkles, AlertTriangle } from "lucide-react";

/**
 * WhyCheckExpert — conversie-blok dat het verschil toont met algemene AI
 * (ChatGPT / Claude). Gebouwd om vertrouwen en urgentie te creëren naast
 * een CTA op Home, Prijzen en intent-pagina's.
 */
const BENEFITS = [
  { Icon: ShieldCheck, title: "Controle op verborgen risico's", text: "Specifiek voor BE/NL wetgeving — geen algemene tips." },
  { Icon: FileSearch, title: "Analyse van jouw documenten en situatie", text: "Wij lezen jouw contract, niet 'een' contract." },
  { Icon: MessageSquareText, title: "Praktisch advies in duidelijke taal", text: "Geen juridisch jargon — concrete onderhandelpunten." },
  { Icon: Wallet, title: "Bespaar tijd, geld en zorgen", text: "Eén rapport voorkomt vaak honderden euro's schade." },
  { Icon: Clock, title: "Resultaat binnen enkele minuten", text: "Upload, betaal, ontvang. Geen wachtlijst, geen afspraken." },
];

export default function WhyCheckExpert({ ctaHref = "/checks", ctaLabel = "Start jouw CheckExpert-analyse", className = "" }) {
  return (
    <section className={`py-16 sm:py-24 ${className}`} data-testid="why-checkexpert-block">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          {/* LEFT — visual mockup (geen stockfoto, Stripe/Notion-style mock) */}
          <div className="relative" data-testid="why-visual">
            {/* AI-twijfel kaart */}
            <div className="absolute -left-2 top-4 w-64 rounded-2xl bg-white border border-border shadow-lg p-4 z-10 rotate-[-4deg]">
              <div className="flex items-center gap-2">
                <div className="h-7 w-7 rounded-full bg-slate-100 flex items-center justify-center">
                  <Sparkles className="h-3.5 w-3.5 text-slate-500" />
                </div>
                <span className="text-xs font-semibold text-ink-soft">ChatGPT / Claude</span>
              </div>
              <p className="mt-2 text-xs text-ink-soft leading-relaxed italic">
                &ldquo;Een huurwaarborg bedraagt meestal 2 tot 3 maanden, afhankelijk van de wetgeving in jouw land...&rdquo;
              </p>
              <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">
                <AlertTriangle className="h-3 w-3" /> algemeen antwoord
              </div>
            </div>

            {/* CheckExpert mock-dashboard */}
            <div className="ml-16 sm:ml-24 rounded-2xl bg-gradient-to-br from-[#001a5e] to-[#002FA7] p-6 sm:p-8 text-white shadow-2xl">
              <div className="flex items-center justify-between mb-5">
                <span className="text-[10px] uppercase tracking-wider font-semibold text-white/70">CheckExpert rapport</span>
                <span className="text-[10px] bg-emerald-400/20 text-emerald-200 rounded-full px-2 py-0.5 font-semibold">live</span>
              </div>

              {/* Score */}
              <div className="flex items-baseline gap-2 mb-1">
                <span className="font-display text-5xl font-bold">82</span>
                <span className="text-white/60 text-sm">/100</span>
              </div>
              <p className="text-white/70 text-xs mb-5">Score voor jouw huurcontract — Vlaanderen</p>

              {/* Findings (mini) */}
              <div className="space-y-2.5">
                {[
                  { c: "bg-emerald-400", t: "Huurwaarborg correct (3 maanden, art. 16 Vlaams Huurdecreet)" },
                  { c: "bg-amber-400", t: "Indexering: clausule mist verplichte basisformule" },
                  { c: "bg-red-400", t: "Opzegtermijn 6 maanden bij vroegtijdige beëindiging — ongeldig" },
                ].map((f, i) => (
                  <div key={i} className="flex items-start gap-2.5 bg-white/5 rounded-lg p-2.5">
                    <span className={`mt-1 inline-block h-2 w-2 rounded-full ${f.c}`} />
                    <span className="text-xs text-white/90 leading-snug">{f.t}</span>
                  </div>
                ))}
              </div>

              <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between text-[11px] text-white/60">
                <span>Klaar in 38 sec</span>
                <span>+ PDF + onderhandel-tips</span>
              </div>
            </div>
          </div>

          {/* RIGHT — value prop */}
          <div data-testid="why-content">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#002FA7]">Vs. ChatGPT &amp; Claude</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-ink mt-3 tracking-tight">
              Waarom vertrouwen op één antwoord als je <span className="text-[#002FA7]">zekerheid</span> kunt krijgen?
            </h2>
            <p className="text-ink-soft mt-3 text-base sm:text-lg leading-relaxed">
              AI geeft algemene informatie. CheckExpert controleert <b>jouw specifieke situatie</b> — afgetoetst aan de actuele Belgische en Nederlandse wetgeving, met aansprakelijkheid en privacy gegarandeerd.
            </p>

            <ul className="mt-7 space-y-4">
              {BENEFITS.map(({ Icon, title, text }) => (
                <li key={title} className="flex items-start gap-3" data-testid={`why-benefit-${title.split(' ')[0].toLowerCase()}`}>
                  <div className="mt-0.5 h-9 w-9 flex-shrink-0 rounded-xl bg-[#002FA7]/10 flex items-center justify-center">
                    <Icon className="h-4 w-4 text-[#002FA7]" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-ink text-sm sm:text-base">{title}</p>
                    <p className="text-ink-soft text-sm">{text}</p>
                  </div>
                </li>
              ))}
            </ul>

            <Link
              to={ctaHref}
              className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#002FA7] hover:bg-[#0033B5] text-white font-semibold shadow-lg shadow-[#002FA7]/20 transition-colors"
              data-testid="why-cta-btn"
            >
              {ctaLabel} <ArrowRight className="h-4 w-4" />
            </Link>
            <p className="text-xs text-ink-soft mt-3">⚖️ Onafhankelijk · 🇧🇪🇳🇱 BE/NL-wetgeving · 🔒 GDPR-conform · ⚡ Resultaat &lt; 1 min</p>
          </div>
        </div>
      </div>
    </section>
  );
}
